import React from 'react';
import { ArrowLeft, Plus, Building2, Share2, Briefcase, Crown, Users } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { $api } from '../../../api';
import { useOrganization } from '../../../contexts/OrganizationContext';
import ErrorMessage from '../../ErrorMessage';
import { Relation } from './RelationCard';
import { RelationGroup } from './RelationGroup';

interface OutgoingRelationsSectionProps {
  onAddRelation: () => void;
  onEntityClick: (entityId: string) => void;
  onEditRelation: (relation: Relation) => void;
  onDeleteRelation: (relation: Relation) => void;
  isDeleting?: boolean;
}

const groupRelationsByKind = (relations: Relation[]) => {
  const groups: Record<string, Relation[]> = {
    director: [],
    executive: [],
    shareholder: [],
    ubo: [],
    other: [],
  };

  relations.forEach(rel => {
    if (groups[rel.relation_kind]) {
      groups[rel.relation_kind].push(rel);
    } else {
      groups.other.push(rel);
    }
  });

  return groups;
};

export const OutgoingRelationsSection: React.FC<OutgoingRelationsSectionProps> = ({
  onAddRelation,
  onEntityClick,
  onEditRelation,
  onDeleteRelation,
  isDeleting
}) => {
  const { clientId, organizationId } = useParams<{ clientId: string; organizationId: string }>();
  const { currentOrganization } = useOrganization();
  const orgId = organizationId || currentOrganization?.id || '';
  const entityId = clientId || '';

  // Fetch relations where current entity is the parent (outgoing relations)
  const parentRelationsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}/relations',
    {
      params: {
        path: {
          organization_id: orgId,
          id: entityId,
        },
        query: {
          role: 'parent',
        },
      },
    },
    {
      enabled: !!orgId && !!entityId,
    }
  );

  const relations = parentRelationsQuery.data?.relations || [];
  const groups = groupRelationsByKind(relations);

  if (parentRelationsQuery.isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
          <span className="ml-3 text-gray-600">Chargement des participations et filiales...</span>
        </div>
      </div>
    );
  }

  if (parentRelationsQuery.error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <ErrorMessage error={parentRelationsQuery.error} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <ArrowLeft className="h-5 w-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Participations et filiales</h3>
        </div>
        <button
          onClick={onAddRelation}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Ajouter une participation</span>
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Entreprises dans lesquelles cette entreprise détient des parts ou a des relations
      </p>

      {relations.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Aucune participation ou filiale enregistrée</p>
        </div>
      ) : (
        <div className="space-y-6">
          <RelationGroup
            title="Filiales"
            relations={groups.shareholder}
            direction="outgoing"
            icon={Building2}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          <RelationGroup
            title="Directeur de"
            relations={groups.director}
            direction="outgoing"
            icon={Briefcase}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          <RelationGroup
            title="Dirigeant de"
            relations={groups.executive}
            direction="outgoing"
            icon={Crown}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          <RelationGroup
            title="UBO (Bénéficiaires effectifs)"
            relations={groups.ubo}
            direction="outgoing"
            icon={Users}
            onEntityClick={onEntityClick}
            onEdit={onEditRelation}
            onDelete={onDeleteRelation}
            isDeleting={isDeleting}
          />
          {groups.other.length > 0 && (
            <RelationGroup
              title="Autres participations"
              relations={groups.other}
              direction="outgoing"
              icon={Share2}
              onEntityClick={onEntityClick}
              onEdit={onEditRelation}
              onDelete={onDeleteRelation}
              isDeleting={isDeleting}
            />
          )}
        </div>
      )}
    </div>
  );
};

